import { Component, OnInit, OnDestroy } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { PrintService } from '../../../service/print.service';
import { AssetsDataService } from '../../../service/assets-data.service';
import { SelectedLocationService } from '../../../service/selected-location.service';
import { PrintRequest } from '../../../models/printing/PrintRequest';
import { PrintResult } from '../../../models/printing/PrintResult';
import { PrinterInfo } from '../../../models/printing/PrinterInfo';

/**
 * PrinterTestComponent
 *
 * Development/testing harness for the print API (`POST {apiUrl}/V1/print`).
 * Lets a tester build a PrintRequest with any combination of options, fire it
 * through PrintService.SubmitPrint, and inspect the full PrintResult — overall
 * success plus the per-document outcomes (kind / success / status code / message).
 *
 * Printer names default to the current facility's configured printers (from the
 * location config, keyed off SelectedLocationService) and can be overridden from
 * the live printer list fetched via PrintService.GetPrinters.
 *
 * This is a testing component only and is reached from the landing page's Testing
 * section.
 */
@Component({
  selector: 'app-printer-test',
  templateUrl: './printer-test.component.html',
  styleUrls: ['./printer-test.component.css'],
})
export class PrinterTestComponent implements OnInit, OnDestroy {
  /** The editable request bound to the form. */
  public request: PrintRequest = this.buildSampleRequest();

  /** The last result returned by the API, rendered in the results panel. */
  public result: PrintResult | null = null;

  /** Error message when the request itself failed (non-200 / network). */
  public errorMessage: string | null = null;

  /** True while a request is in flight. */
  public isSubmitting = false;

  /** Label of the current facility (from SelectedLocationService), for display. */
  public locationLabel = '';

  /** True if the current facility has any default printer configured. */
  public locationHasDefaults = false;

  /** Printers reported by the print API, for the optional override dropdowns. */
  public printers: PrinterInfo[] = [];

  /** True while the printer list is loading. */
  public printersLoading = false;

  /** True once a printer-list fetch has completed (success or empty). */
  public printersLoaded = false;

  private _subscriptions = new Subscription();

  constructor(
    private _modalController: ModalController,
    private _printService: PrintService,
    private _assetsDataService: AssetsDataService,
    private _selectedLocationService: SelectedLocationService
  ) {}

  ngOnInit(): void {
    this.applyCurrentLocationDefaults();

    this._subscriptions.add(
      this._printService.availablePrinters.subscribe((printers) => {
        this.printers = printers;
        if (printers.length > 0) {
          this.printersLoaded = true;
        }
      })
    );
    this._subscriptions.add(
      this._printService.printersLoading.subscribe((loading) => {
        this.printersLoading = loading;
        if (!loading) {
          this.printersLoaded = true;
        }
      })
    );
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  /** Fetches the available printer list so the override dropdowns can be populated. */
  public loadPrinters(): void {
    this._printService.fetchPrinters();
  }

  /**
   * Submits the current request via the print service and captures the result.
   * Uses SubmitPrint directly (rather than submitPrintJob) so the full
   * per-document outcome detail can be shown in the panel.
   */
  public submit(): void {
    this.isSubmitting = true;
    this.result = null;
    this.errorMessage = null;

    // Empty-string dates fail API date binding — normalize to null before sending.
    const payload: PrintRequest = {
      ...this.request,
      dateIn: this.request.dateIn || null,
      estimatedDateOut: this.request.estimatedDateOut || null,
    };

    this._printService.SubmitPrint(payload).subscribe({
      next: (result: PrintResult) => {
        this.result = result;
      },
      error: (err) => {
        console.error('Test print failed:', err);
        this.errorMessage = err?.message ?? 'Request failed.';
        this.isSubmitting = false;
      },
      complete: () => {
        this.isSubmitting = false;
      },
    });
  }

  /** Pretty-prints the request that will be sent, for quick inspection. */
  public get requestPreview(): string {
    return JSON.stringify(this.request, null, 2);
  }

  /** Closes the modal. */
  public close(): void {
    this._modalController.dismiss();
  }

  /**
   * Loads the current facility's default printer names into the request.
   * The facility comes from SelectedLocationService; its defaults live in the
   * location config. Only fields with a configured default are set, so a blank
   * default (most locations, for now) leaves the sample value alone.
   */
  private applyCurrentLocationDefaults(): void {
    const selected = this._selectedLocationService.getSelectedLocation();
    this.locationLabel = selected.label;

    const location = this._assetsDataService
      .GetLocationData()
      .find((loc) => loc.locationName === selected.name);

    this.locationHasDefaults = !!(
      location?.zebraPrinterName || location?.copierPrinterName
    );

    if (location?.zebraPrinterName) {
      this.request.zebraPrinterName = location.zebraPrinterName;
    }
    if (location?.copierPrinterName) {
      this.request.copierPrinterName = location.copierPrinterName;
    }
  }

  /** Builds a valid sample payload. `kind` selects which documents are printed. */
  private buildSampleRequest(): PrintRequest {
    return {
      kind: 'Both',
      zebraPrinterName: 'SHOP-ZEBRA-01',
      copierPrinterName: 'SHOP-COPIER-02',
      jobNumber: '12345',
      poNumber: 'PO-9981',
      labelCount: 1,
      copyCount: 1,
      vendor: 'Acme Supply',
      jobName: 'North Tower HVAC',
      jcDept: '06',
      type: 'Delivery',
      qtyUnits: '5',
      shelfLocation: 'A-12',
      shopSqFt: 40,
      estimatedDateOut: null,
      notify: 'pm@macmiller.com',
      contact: 'Jane Tech',
      items: '2 pallets ductwork',
      notes: 'Fragile',
      dateIn: null,
     
    };
  }
}
